import { useEffect } from 'react';
import { Inputs, useInputs } from './hooks/useInputs';
import { BrowserRouter, Route, Switch, Redirect, useLocation, RouteProps } from 'react-router-dom';
import { Box } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ErrorBoundary from './components/errorBoundary/ErrorBoundary';
import Header from './components/header/Header';
import Footer from './components/footer/Footer';
import Loading from './components/loading/Loading';
import Homepage from './screens/homepage/Homepage';
import IncomeQualified from './screens/incomeQualified/IncomeQualified';
import EvOwners from './screens/evOwners/EvOwners';
import NotFound from './screens/error/NotFound';
import DevToolsRoutes from './screens/devtools/DevToolsRoutes';
import DisplayProvider from './context/DisplayProvider';
import ReactIntlProvider from './utils/ReactIntlProvider';
import UserPrefsProvider from './context/UserPrefsProvider';
import theme from './config/theme';
import VehicleDetails from './screens/vehicleDetails/VehicleDetails';
import UsedVehicleDetails from './screens/vehicleDetails/UsedVehicleDetails';
import VehicleCatalog from './screens/vehicleCatalog/VehicleCatalog';
import ChargingStationDetails from './screens/chargingDetails/ChargingStationDetails';
import Rates from './screens/rates/Rates';
import FAQ from './screens/faq/FAQ';
import ChargingFAQ from './screens/chargingFaq/ChargingFAQ';
import Incentives from './screens/incentives/Incentives';
import Compare from './screens/compare/Compare';
import ApiCacheProvider from './context/apiCacheProvider';
import Chart from 'chart.js/auto';
import GaTracker from './utils/GaTracker/GaTracker';
import FacebookTracker from './utils/FacebookTracker/FacebookTracker';
import { useUuid } from './api/hooks/useUuid';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import Map from './screens/map/Map';
import FeedbackFixedButton from './components/feedback/FeedbackFixedButton';
import VehicleCacheProvider from './context/VehicleCacheProvider';
import Search from './screens/Search/Search';
import { RateCalculator } from '@bellawatt/electric-rate-engine';
import DeployedBranchName from './components/deployedBranchName/DeployedBranchName';
import Honeybadger from '@honeybadger-io/js';
import useResetZipOnNav from './utils/hooks/useResetZipOnNav';
import InvalidZipcodeAlert from './components/invalidZipcodeAlert/InvalidZipcodeAlert';
import useScrollToTop from './utils/useScrollToTop';

RateCalculator.shouldValidate = process.env.RATE_CALCULATOR_VALIDATION === 'true';
RateCalculator.shouldLogValidationErrors = process.env.RATE_CALCULATOR_VALIDATION === 'true';

Honeybadger.configure({
  apiKey: process.env.REACT_APP_HONEYBADGER_API_KEY,
  environment: process.env.REACT_APP_ENVIRONMENT,
  revision: 'main',
  debug: false,
});

Chart.defaults.font.family = 'din';

export function AppProviders({ children }: { children: JSX.Element }) {
  const cache = createCache({
    key: 'css',
    prepend: true,
    nonce: 'm7yvr64n3ob2ci9xdpl',
  });

  return (
    <BrowserRouter>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Inputs computed={{}} defaults={{}} ignore={[]}>
            <ApiCacheProvider>
              <ReactIntlProvider>
                <VehicleCacheProvider>
                  <UserPrefsProvider>
                    <ErrorBoundary>
                      <DisplayProvider>{children}</DisplayProvider>
                    </ErrorBoundary>
                  </UserPrefsProvider>
                </VehicleCacheProvider>
              </ReactIntlProvider>
            </ApiCacheProvider>
          </Inputs>
        </ThemeProvider>
      </CacheProvider>
    </BrowserRouter>
  );
}

const BASE_TITLE = 'EV Savings Calculator - PG&E';

const Page = ({ title, ...rest }: RouteProps & { title: string }) => {
  useEffect(() => {
    document.title = title ? `${BASE_TITLE} - ${title}` : BASE_TITLE;
  }, [title]);
  return <Route {...rest} />;
};

function AppRoutes() {
  useScrollToTop();

  const { loading } = useInputs();
  const location = useLocation();
  useResetZipOnNav(location.pathname);

  const { uuid } = useUuid();

  useEffect(() => {
    GaTracker.initialize({
      nonce: 'm7yvr64n3ob2ci9xdpl',
      gaOptions: {
        userId: uuid,
      },
    });
    FacebookTracker.initialize();
  }, [uuid]);

  useEffect(() => {
    FacebookTracker.track();
  }, [location, uuid]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Box display="flex" flexDirection="column" height="100%" justifyContent="stretch">
        <div style={{ display: 'none' }} />
        <Header />
        <Box
          flex="1 1"
          display="flex"
          flexDirection="column"
          height="100%"
          sx={{ overflowY: 'auto' }}
          id="main-scroll"
        >
          <Box component="main" id="main-content" flex="1 1">
            <Switch>
              <Page title="" exact path="/news/:id">
                <Redirect to="/" />
              </Page>
              <Page title="" exact path="/news">
                <Redirect to="/" />
              </Page>
              <Page title="Incentives" exact path="/incentives" component={Incentives} />
              <Page title="FAQ" exact path="/faq" component={FAQ} />
              <Page title="Charging FAQ" exact path="/charging-faq" component={ChargingFAQ} />
              <Page title="Rates" exact path="/rates" component={Rates} />
              <Page title="Vehicle" exact path="/vehicles/:id" component={VehicleDetails} />
              <Page
                title="Used Vehicle"
                exact
                path="/used-vehicles/:id"
                component={UsedVehicleDetails}
              />
              <Page
                title="Used Vehicle of Year"
                exact
                path="/used-vehicles/:id/:year"
                component={UsedVehicleDetails}
              />
              <Page title="Vehicles" exact path="/vehicles" component={VehicleCatalog} />
              <Page title="Compare" exact path="/compare" component={Compare} />
              <Page title="Charging Stations" exact path="/charging-stations" component={Map} />
              <Page title="Search" exact path="/search" component={Search} />
              <Page
                title="Charging Station Availability"
                exact
                path="/charging-station-availability"
                component={Map}
              />
              <Page
                title="Charging Station Availability"
                exact
                path="/charging-station-availability/:id"
                component={ChargingStationDetails}
              />
              <Page title="Map" exact path="/map-a-route" component={Map} />
              <Page title="Travel Range" exact path="/travel-range" component={Map} />
              <Page title="Dealers" exact path="/dealers" component={Map} />
              <Page title="" exact path="/" component={Homepage} />
              <Page
                title="Income Qualified"
                exact
                path="/income-qualified"
                component={IncomeQualified}
              />
              <Page title="EV Owners" exact path="/ev-owners" component={EvOwners} />
              <Page title="Dev" path="/dev">
                {/* avoid 404 for the /dev Pages below (they break Switch) */}
              </Page>
              <Page title="Not Found" path="*" component={NotFound} />
            </Switch>
            {process.env.REACT_APP_ENVIRONMENT !== 'production' && <DevToolsRoutes />}
          </Box>
          <Footer />
        </Box>
      </Box>
      <FeedbackFixedButton />
      <InvalidZipcodeAlert />
      <DeployedBranchName />
    </>
  );
}

function App() {
  return (
    <AppProviders>
      <AppRoutes />
    </AppProviders>
  );
}

export default App;
